import type { OpsSchedule, RepeatSkip } from '@/services/opsRota/types';

import { shortDayLabel, toastDateLabel } from './dates';
import { clockLabel } from './scheduleForm';

// Copy for the Operations page (GRAVY-10878). Toasts follow the prototype
// (title + body); the delete dialog keeps the ticket's exact wording.

type Times = Pick<OpsSchedule, 'start_time' | 'end_time'>;

/**
 * "8:00 PM" with a non-breaking space, so a message never wraps inside
 * a time. Applies to our copy and to graphy's (overlap errors, skip reasons).
 */
export const keepTimesWhole = (text: string): string =>
  text.replace(/(\d{1,2}:\d{2}) ?(AM|PM)\b/g, '$1\u00a0$2');

/** Ticket form, no spaces: "8:00 AM-5:00 PM" (delete dialog). */
export const blockRange = (s: Times): string =>
  `${clockLabel(s.start_time)}-${clockLabel(s.end_time)}`;

/** Prototype form, spaced: "8:00 AM - 5:00 PM" (block list). */
export const blockRangeSpaced = (s: Times): string =>
  `${clockLabel(s.start_time)} - ${clockLabel(s.end_time)}`;

/** "A", "A and B", "A, B and C" */
export const joinNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export type ToastCopy = { title: string; body?: string };

/** Add, single date or range: N = managers in the block. */
export const savedToast = (
  managers: number,
  date: string,
  endDate?: string | null,
): ToastCopy => ({
  title: 'New ops schedule saved!',
  body: `Scheduled ${managers} ops for ${toastDateLabel(date, endDate)}.`,
});

export const updatedToast = (managers: number, date: string): ToastCopy => ({
  title: 'Ops schedule updated!',
  body: `Scheduled ${managers} ops for ${toastDateLabel(date)}.`,
});

/** Skips named (toast and inline) before the rest are only counted. */
export const MAX_TOAST_SKIPS = 3;

const skipLine = (s: RepeatSkip) =>
  keepTimesWhole(`${s.full_name} on ${shortDayLabel(s.date)} (${s.reason}).`);

/**
 * " A. B. C. Skipped N more." A long repeat can skip dozens of manager-days:
 * name the first few and count the rest, so the text stays readable.
 */
const skipSentences = (
  skipped: RepeatSkip[],
  line: (s: RepeatSkip) => string,
): string => {
  const named = skipped.slice(0, MAX_TOAST_SKIPS);
  const rest = skipped.length - named.length;
  return (
    named.map((s) => ` ${line(s)}`).join('') +
    (rest > 0 ? ` Skipped ${rest} more.` : '')
  );
};

/**
 * Repeat: the range of the dates written (`result.schedules[].date`),
 * plus any managers graphy skipped.
 */
export const repeatedToast = (
  managers: number,
  dates: string[],
  skipped: RepeatSkip[] = [],
): ToastCopy => {
  const sorted = Array.from(new Set(dates)).sort();
  const skips = skipSentences(skipped, (s) => `Skipped ${skipLine(s)}`);
  const when =
    sorted.length > 0
      ? ` for ${toastDateLabel(sorted[0], sorted[sorted.length - 1])}`
      : '';
  return {
    title: 'New ops schedule saved!',
    body: `Scheduled ${managers} ops${when}.${skips}`,
  };
};

export const DELETED_TOAST: ToastCopy = { title: 'Schedule deleted.' };

/** 409, or the block was deleted meanwhile (404): same advice either way. */
export const STALE_MESSAGE =
  'This schedule was changed by someone else. Reload to see the latest.';

export const DELETE_TITLE = 'Delete schedule';

/** Verbatim from the ticket. */
export const deleteConfirmText = (s: OpsSchedule): string =>
  keepTimesWhole(
    `Delete the ${blockRange(s)} schedule on ${shortDayLabel(s.date)}? ` +
      `${joinNames(s.managers.map((m) => m.full_name))} will be removed.`,
  );

export const EMPTY_DAY = 'No ops scheduled for this day.';
export const LOADING = 'Loading…';
export const LOAD_ERROR = "Couldn't load schedules.";
/** a background refresh failed; the cached schedules stay. */
export const REFRESH_ERROR = "Couldn't refresh schedules.";
export const PREVIEW_LOAD_ERROR = "Couldn't load ops for this day.";
export const DISCARD_TITLE = 'Discard unsaved changes?';
export const DISCARD_MESSAGE =
  'Your changes to this schedule have not been saved.';

/** the roster is empty, so nobody can be scheduled. */
export const NO_MANAGERS = 'No operations managers yet';
export const MANAGERS_LOAD_ERROR = "Couldn't load managers.";
/** BR-06: a deactivated manager still on the block blocks the save. */
export const INACTIVE_CHIP_NOTE = '(inactive — remove to save)';
export const ENDS_NEXT_DAY = 'Ends the next day';
export const SAVE_FAILED = 'Something went wrong. Try again.';

// ---- Repeat modal -------------------------------------------------------

export const REPEAT_TITLE = 'Pick a date to repeat schedule';

/** 404 on repeat: the source block is gone (there is no Reload here). */
export const REPEAT_SOURCE_GONE =
  'This schedule was deleted by someone else. Close this and pick another block.';

/**
 * Every picked date was skipped: the modal stays open and says why. With no
 * skips, graphy wrote nothing because each date already holds this exact
 * block with everyone on it (repeating onto the block's own date).
 */
export const nothingRepeatedText = (skipped: RepeatSkip[]): string =>
  skipped.length === 0
    ? 'Nothing was repeated. Those dates already have this schedule.'
    : `Nothing was repeated.${skipSentences(skipped, skipLine)}`;

// ---- roster --------------------------------------------------------

export const ROSTER_TITLE = 'Manage managers';
export const ROSTER_EMPTY =
  'No operations managers yet. Add one to start scheduling.';
export const ROSTER_LOAD_ERROR = "Couldn't load the roster.";
export const DELETE_MANAGER_TITLE = 'Delete manager';
export const deleteManagerText = (fullName: string): string =>
  `Delete ${fullName} from the ops roster? This cannot be undone.`;
